import { useEffect, useState } from "react";
import type { ResolvedGuardianInviteDto } from "@madamuh/domain";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { consumeGuardianInvite, resolveGuardianInvite } from "../../shared/api/guardian";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { PageScaffold } from "../../shared/ui/PageScaffold";

type ResolveState = "idle" | "loading" | "ready" | "error";
type ConsumeState = "idle" | "submitting" | "success" | "error";

export function LinkStudentConfirmPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const [searchParams] = useSearchParams();
  const { status, errorMessage, bootstrapSession } = useSessionBootstrap();
  const [resolvedInvite, setResolvedInvite] = useState<ResolvedGuardianInviteDto | null>(null);
  const [resolveState, setResolveState] = useState<ResolveState>("idle");
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [consumeState, setConsumeState] = useState<ConsumeState>("idle");
  const [consumeMessage, setConsumeMessage] = useState<string | null>(null);
  const [lastResolvedKey, setLastResolvedKey] = useState<string | null>(null);

  const inviteToken = searchParams.get("inviteToken")?.trim() ?? "";
  const manualCode = searchParams.get("manualCode")?.trim() ?? "";
  const resolutionKey = inviteToken ? `token:${inviteToken}` : manualCode ? `code:${manualCode}` : null;

  useEffect(() => {
    if (status === "idle") {
      void bootstrapSession(tenantSlug);
    }
  }, [bootstrapSession, status, tenantSlug]);

  useEffect(() => {
    setResolvedInvite(null);
    setResolveError(null);
    setResolveState("idle");
    setConsumeState("idle");
    setConsumeMessage(null);
    setLastResolvedKey(null);
  }, [resolutionKey]);

  useEffect(() => {
    if (status !== "authenticated" || !resolutionKey || resolutionKey === lastResolvedKey) {
      return;
    }

    let cancelled = false;

    async function runResolve() {
      setResolveState("loading");
      setResolveError(null);

      try {
        const nextInvite = await resolveGuardianInvite(tenantSlug, {
          inviteToken: inviteToken || null,
          manualCode: manualCode || null
        });

        if (cancelled) {
          return;
        }

        setResolvedInvite(nextInvite);
        setResolveState("ready");
        setLastResolvedKey(resolutionKey);
      } catch (error) {
        if (cancelled) {
          return;
        }

        setResolvedInvite(null);
        setResolveState("error");
        setResolveError(error instanceof Error ? error.message : "招待内容の取得に失敗しました");
      }
    }

    void runResolve();

    return () => {
      cancelled = true;
    };
  }, [bootstrapSession, inviteToken, lastResolvedKey, manualCode, resolutionKey, status, tenantSlug]);

  async function handleConsumeInvite() {
    if (!resolvedInvite) {
      return;
    }

    setConsumeState("submitting");
    setConsumeMessage(null);

    try {
      const response = await consumeGuardianInvite(tenantSlug, {
        inviteId: resolvedInvite.inviteId
      });
      const nextSession = await bootstrapSession(tenantSlug);

      setConsumeState("success");
      setConsumeMessage(
        response.alreadyConsumed
          ? "この招待はすでにこのアカウントで消費済みです。family モードへ戻します。"
          : "招待を消費しました。family モードへ遷移します。"
      );

      if (nextSession) {
        navigate(nextSession.entryPath, { replace: true });
      }
    } catch (error) {
      setConsumeState("error");
      setConsumeMessage(error instanceof Error ? error.message : "招待消費に失敗しました");
    }
  }

  return (
    <PageScaffold
      title="Link Student Confirm"
      description="招待URL / 招待コードの解決結果を確認し、保護者紐付けを消費する画面です。"
      links={[
        { to: "/t/:tenantSlug/link-student", label: "入力へ戻る" },
        { to: "/t/:tenantSlug/link-student/request", label: "申請へ進む" }
      ]}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {!resolutionKey ? (
          <p>
            招待URL token か招待コードがありません。<Link to={`/t/${tenantSlug}/link-student`}>入力画面</Link>
            からやり直してください。
          </p>
        ) : null}

        {status === "loading" ? <p>セッションを確認しています。</p> : null}
        {status === "unauthenticated" ? (
          <p>
            招待確認には session が必要です。<Link to={`/t/${tenantSlug}/entry`}>/entry</Link> で mock login を実行してください。
          </p>
        ) : null}
        {status === "error" ? <p>セッション取得に失敗しました: {errorMessage}</p> : null}

        {status === "authenticated" && resolutionKey ? (
          <>
            {resolveState === "loading" ? <p>招待内容を確認しています。</p> : null}
            {resolveState === "error" ? (
              <p>
                招待内容の取得に失敗しました。
                {resolveError ? ` ${resolveError}` : ""}
              </p>
            ) : null}

            {resolvedInvite ? (
              <div style={{ display: "grid", gap: 12 }}>
                <div>
                  <strong>scope</strong>: {resolvedInvite.scopeType}
                </div>
                <div>
                  <strong>relation</strong>: {resolvedInvite.relationTypeDefault}
                </div>
                <div>
                  <strong>status</strong>: {resolvedInvite.status}
                </div>
                <div>
                  <strong>remaining uses</strong>: {resolvedInvite.remainingUseCount} / {resolvedInvite.maxUseCount}
                </div>
                <div>
                  <strong>expiresAt</strong>: {resolvedInvite.expiresAt}
                </div>
                <div>
                  <strong>students</strong>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                    {resolvedInvite.students.map((student) => (
                      <li key={student.studentId}>
                        {student.displayName} ({student.studentId})
                      </li>
                    ))}
                  </ul>
                </div>
                {resolvedInvite.alreadyConsumedByCurrentUser ? (
                  <p style={{ margin: 0 }}>
                    このアカウントではすでに同じ招待を消費済みです。再実行しても link と usage は増えません。
                  </p>
                ) : null}

                <button
                  type="button"
                  onClick={() => void handleConsumeInvite()}
                  disabled={
                    consumeState === "submitting" ||
                    (resolvedInvite.status !== "active" && !resolvedInvite.alreadyConsumedByCurrentUser)
                  }
                >
                  {consumeState === "submitting" ? "処理中..." : "この招待を消費する"}
                </button>

                {consumeMessage ? <p style={{ margin: 0 }}>{consumeMessage}</p> : null}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </PageScaffold>
  );
}
